/**
 * V37.60 — Panneau Assistant IA VEFA Programme immobilier.
 *
 * Fonctionnalites :
 *  - Selection d'un projet VEFA existant
 *  - Import de PDF contrat VEFA
 *  - Lancement de l'analyse IA (checklist VEFA strictement separee de CCMI)
 *  - Affichage des resultats (score, risque, manquements, vigilance)
 *  - Revue humaine (3 decisions)
 *  - Telechargement du rapport PDF
 *
 * Garanties strictes :
 *  - Ne jamais analyser un VEFA avec la checklist CCMI
 *  - Aucun impact sur ccmi_projects, LEGALCOOP, DOS DOMIUM ou paiements
 *  - Aucune modification automatique du statut du projet VEFA
 *  - Rappel disclaimers VEFA + obligation notariale dans l'UI
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Brain, Upload, Loader2, Download, AlertTriangle, ShieldCheck, CheckCircle2, AlertOctagon, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RISQUE_COLOR = {
  faible: 'bg-emerald-700 text-white border-emerald-900',
  moyen:  'bg-amber-600 text-white border-amber-800',
  eleve:  'bg-red-700 text-white border-red-900',
};

const getAuthHeader = () => {
  const t = localStorage.getItem('token')
        || localStorage.getItem('access_token')
        || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function VefaAiAssistantPanel() {
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [documents, setDocuments] = useState([]);
  const [selectedDocId, setSelectedDocId] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [reviewComment, setReviewComment] = useState('');
  const [currentDoc, setCurrentDoc] = useState(null);

  const fetchProjects = useCallback(async () => {
    setLoadingProjects(true);
    try {
      const res = await axios.get(`${API}/ccmi/vefa/projects?limit=100`, { headers: getAuthHeader() });
      setProjects(res.data?.items || []);
    } catch { /* silencieux */ } finally {
      setLoadingProjects(false);
    }
  }, []);

  const fetchDocuments = useCallback(async (pid) => {
    if (!pid) { setDocuments([]); return; }
    try {
      const res = await axios.get(`${API}/ccmi/vefa/projects/${pid}/documents`, { headers: getAuthHeader() });
      setDocuments(res.data?.documents || []);
    } catch { setDocuments([]); }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);
  useEffect(() => { fetchDocuments(selectedId); setSelectedDocId(''); setCurrentDoc(null); }, [selectedId, fetchDocuments]);

  useEffect(() => {
    if (!selectedDocId) { setCurrentDoc(null); return; }
    const found = documents.find((d) => d.document_id === selectedDocId);
    setCurrentDoc(found || null);
  }, [selectedDocId, documents]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      // V38.02 — Si aucun projet sélectionné, on crée automatiquement
      // un projet VEFA minimal pour permettre l'import direct du PDF.
      let projectId = selectedId;
      let createdProjectRef = null;
      if (!projectId) {
        // V38.03 — Référence lisible « VEFA-DRAFT-YYYY-XXXX » (hex 4) pour
        // distinguer les projets auto-créés via upload des projets nominaux.
        const year = new Date().getFullYear();
        const suffix = Math.floor(Math.random() * 0xFFFF)
          .toString(16).toUpperCase().padStart(4, '0');
        const draftRef = `VEFA-DRAFT-${year}-${suffix}`;
        const createRes = await axios.post(
          `${API}/ccmi/vefa/projects`,
          { reference: draftRef, statut: 'reservation_preparee' },
          { headers: getAuthHeader() },
        );
        projectId = createRes.data?.project?.id;
        createdProjectRef = createRes.data?.project?.reference;
        if (!projectId) {
          throw new Error('Création projet VEFA échouée');
        }
        toast.success(`Projet VEFA créé : ${createdProjectRef || projectId}`);
      }

      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/ccmi/vefa/projects/${projectId}/contract-import`,
        fd,
        { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`PDF VEFA importé (${res.data?.document_id})`);

      // Si on a créé un nouveau projet, on rafraîchit la liste puis on le sélectionne
      if (createdProjectRef !== null) {
        await fetchProjects();
        setSelectedId(projectId);
      }
      await fetchDocuments(projectId);
      setSelectedDocId(res.data?.document_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Erreur import PDF VEFA');
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!selectedId || !selectedDocId) return;
    setAnalyzing(true);
    try {
      const res = await axios.post(
        `${API}/ccmi/vefa/projects/${selectedId}/documents/${selectedDocId}/analyze`,
        {},
        { headers: getAuthHeader() },
      );
      toast.success('Analyse IA VEFA effectuée');
      await fetchDocuments(selectedId);
      setCurrentDoc((cur) => cur ? { ...cur, ai_analysis: res.data.analysis } : cur);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur analyse IA');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleReview = async (decision) => {
    if (!selectedId || !selectedDocId) return;
    if (decision === 'pret_pour_validation_juridique' && (reviewComment || '').trim().length < 20) {
      toast.error('Commentaire ≥ 20 caractères requis pour "Prêt pour validation juridique"');
      return;
    }
    setReviewing(true);
    try {
      const res = await axios.post(
        `${API}/ccmi/vefa/projects/${selectedId}/documents/${selectedDocId}/ai-review`,
        { decision, comment: reviewComment || null },
        { headers: getAuthHeader() },
      );
      toast.success(`Revue enregistrée (${decision.replace(/_/g, ' ')})`);
      setCurrentDoc((cur) => cur ? { ...cur, ai_analysis_review: res.data.review } : cur);
      setReviewComment('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur enregistrement revue');
    } finally {
      setReviewing(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!selectedId || !selectedDocId) return;
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/ccmi/vefa/projects/${selectedId}/documents/${selectedDocId}/review-report-pdf`,
        {},
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_revue_vefa_${selectedDocId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Rapport VEFA téléchargé');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur téléchargement rapport');
    } finally {
      setDownloading(false);
    }
  };

  const ai = currentDoc?.ai_analysis;
  const review = currentDoc?.ai_analysis_review;
  const result = ai?.result;
  const hasAi = ai?.status === 'success';

  return (
    <Card className="border-amber-300" data-testid="vefa-ai-assistant-panel">
      <CardHeader className="bg-amber-50 border-b border-amber-200">
        <CardTitle className="flex items-center gap-2 text-amber-900">
          <Brain className="h-5 w-5" />
          Assistant IA VEFA Programme immobilier
        </CardTitle>
        <CardDescription className="text-amber-800">
          Revue documentaire VEFA basée sur la checklist L261-1 et s. — grille séparée de
          la checklist CCMI. L'acte définitif VEFA doit obligatoirement être reçu par notaire.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        {/* Selection projet */}
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">Projet VEFA</label>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger className="font-semibold text-slate-900 border-slate-400 bg-white" data-testid="vefa-ai-project-select">
                <SelectValue placeholder={loadingProjects ? 'Chargement...' : 'Sélectionner un projet VEFA'} />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.reference} — {p.statut}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">Document VEFA</label>
            <Select value={selectedDocId} onValueChange={setSelectedDocId} disabled={!selectedId}>
              <SelectTrigger className="font-semibold text-slate-900 border-slate-400 bg-white" data-testid="vefa-ai-document-select">
                <SelectValue placeholder={selectedId ? 'Sélectionner un document' : '— Choisir d\'abord un projet'} />
              </SelectTrigger>
              <SelectContent>
                {documents.length === 0 ? (
                  <SelectItem value="__none__" disabled>Aucun document</SelectItem>
                ) : documents.map((d) => (
                  <SelectItem key={d.document_id} value={d.document_id}>
                    {d.document_id} — {d.filename}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Actions principales */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-200">
          <label
            className={`inline-flex items-center gap-2 px-3 py-2 rounded text-sm font-semibold text-white cursor-pointer ${
              !uploading ? 'bg-blue-700 hover:bg-blue-800' : 'bg-slate-400 cursor-not-allowed'
            }`}
            data-testid="vefa-ai-upload-btn"
            title={selectedId ? 'Importer un PDF dans le projet sélectionné' : 'Importer un PDF — un projet VEFA sera créé automatiquement'}
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Importer contrat VEFA PDF
            <input
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              disabled={uploading}
              onChange={(e) => { if (e.target.files?.[0]) handleUpload(e.target.files[0]); e.target.value=''; }}
            />
          </label>

          <Button
            onClick={handleAnalyze}
            disabled={!selectedDocId || analyzing}
            className="bg-amber-700 hover:bg-amber-800 text-white"
            data-testid="vefa-ai-analyze-btn"
          >
            {analyzing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Brain className="h-4 w-4 mr-1" />}
            Analyser (IA VEFA)
          </Button>

          {hasAi && (
            <Button
              onClick={handleDownloadReport}
              disabled={downloading}
              variant="outline"
              className="border-amber-800 text-amber-900 font-semibold"
              data-testid="vefa-ai-download-report-btn"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
              Télécharger rapport
            </Button>
          )}
        </div>

        {/* Resultat IA */}
        {currentDoc && (
          <div className="border border-slate-200 rounded p-3 bg-white space-y-3" data-testid="vefa-ai-result-block">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-xs text-slate-600">
                <span className="font-mono">{currentDoc.document_id}</span> • {currentDoc.filename}
                {' '}• {currentDoc.pages_count} page{currentDoc.pages_count > 1 ? 's' : ''}
                {' '}• <span className="font-mono">{currentDoc.sha256?.slice(0, 12)}…</span>
              </div>
              {currentDoc.requires_ocr && (
                <Badge className="bg-amber-600 text-white border-amber-800 font-semibold text-[10px]">
                  OCR requis avant analyse
                </Badge>
              )}
            </div>

            {!ai && (
              <p className="text-sm text-slate-500 italic">
                Aucune analyse IA effectuée. Cliquez sur « Analyser (IA VEFA) ».
              </p>
            )}

            {ai && ai.status !== 'success' && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-300 rounded p-2">
                Analyse IA : <b>{ai.status}</b> {ai.error_message ? `— ${ai.error_message}` : null}
              </div>
            )}

            {hasAi && result && (
              <>
                <div className="flex items-center gap-3 flex-wrap" data-testid="vefa-ai-score-row">
                  <Badge className={`text-[11px] font-semibold ${RISQUE_COLOR[result.risque_global] || RISQUE_COLOR.moyen}`}>
                    Risque {result.risque_global}
                  </Badge>
                  <span className="text-sm font-semibold text-slate-800">
                    Score conformité VEFA : <span className="text-amber-900">{result.score_conformite} / 100</span>
                  </span>
                  <span className="text-xs text-slate-500">
                    Checklist : <span className="font-mono">{ai.checklist_used || 'vefa'}</span> • {ai.model}
                  </span>
                </div>
                {result.resume && (
                  <p className="text-sm text-slate-700 italic border-l-4 border-amber-400 pl-3">
                    « {result.resume} »
                  </p>
                )}
                <div className="grid md:grid-cols-3 gap-3">
                  <ResultColumn icon={<CheckCircle2 className="h-4 w-4 text-emerald-700" />} title="Mentions présentes" items={result.presentes} bullet="emerald" />
                  <ResultColumn icon={<AlertOctagon className="h-4 w-4 text-red-700" />} title="Manquements" items={result.manquements} bullet="red" />
                  <ResultColumn icon={<AlertTriangle className="h-4 w-4 text-amber-700" />} title="Vigilance" items={result.vigilance} bullet="amber" />
                </div>

                {/* Revue humaine */}
                <div className="border-t border-slate-200 pt-3 mt-2 space-y-2" data-testid="vefa-ai-review-block">
                  <p className="text-sm font-semibold text-slate-800">Revue humaine post-IA</p>
                  {review && (
                    <div className="text-xs bg-slate-50 border border-slate-200 rounded p-2">
                      <Badge className="bg-blue-700 text-white border-blue-900 text-[10px] mr-2 font-semibold">
                        {(review.decision || '').replace(/_/g, ' ')}
                      </Badge>
                      <span className="text-slate-700">par {review.reviewed_by} le {new Date(review.reviewed_at).toLocaleString('fr-FR')}</span>
                      {review.comment && (
                        <p className="mt-1 text-slate-700 italic">« {review.comment} »</p>
                      )}
                    </div>
                  )}
                  <Textarea
                    placeholder="Commentaire reviewer (obligatoire ≥ 20 caractères pour « Prêt pour validation juridique »)"
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    className="text-sm border-slate-300"
                    data-testid="vefa-ai-review-comment"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => handleReview('a_corriger')} disabled={reviewing}
                      className="bg-red-700 hover:bg-red-800 text-white" data-testid="vefa-ai-decision-a_corriger">
                      <AlertOctagon className="h-3 w-3 mr-1" />À corriger
                    </Button>
                    <Button size="sm" onClick={() => handleReview('acceptable')} disabled={reviewing}
                      className="bg-amber-700 hover:bg-amber-800 text-white" data-testid="vefa-ai-decision-acceptable">
                      <AlertTriangle className="h-3 w-3 mr-1" />Acceptable
                    </Button>
                    <Button size="sm" onClick={() => handleReview('pret_pour_validation_juridique')} disabled={reviewing}
                      className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="vefa-ai-decision-pret">
                      <ShieldCheck className="h-3 w-3 mr-1" />Prêt validation juridique
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Disclaimers VEFA obligatoires */}
        <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded p-2 space-y-1" data-testid="vefa-ai-disclaimers">
          <p className="flex items-start gap-1"><ChevronRight className="h-3 w-3 mt-0.5 flex-shrink-0" /> Aide à la revue documentaire VEFA — ne constitue pas un avis juridique définitif.</p>
          <p className="flex items-start gap-1"><ChevronRight className="h-3 w-3 mt-0.5 flex-shrink-0" /> L'acte définitif VEFA doit être reçu par notaire.</p>
          <p className="flex items-start gap-1"><ChevronRight className="h-3 w-3 mt-0.5 flex-shrink-0" /> Pour une consultation juridique formelle, contactez un avocat, un notaire ou un professionnel habilité.</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ResultColumn({ icon, title, items, bullet }) {
  const colorMap = {
    emerald: 'border-emerald-200 bg-emerald-50',
    red: 'border-red-200 bg-red-50',
    amber: 'border-amber-200 bg-amber-50',
  };
  return (
    <div className={`rounded border ${colorMap[bullet] || 'border-slate-200 bg-slate-50'} p-2`}>
      <div className="flex items-center gap-1 text-xs font-semibold text-slate-800 mb-1">
        {icon}{title} ({(items || []).length})
      </div>
      {(items || []).length === 0 ? (
        <p className="text-xs italic text-slate-500">—</p>
      ) : (
        <ul className="text-xs text-slate-700 space-y-1 max-h-40 overflow-y-auto">
          {items.map((it, i) => <li key={i} className="leading-snug">• {it}</li>)}
        </ul>
      )}
    </div>
  );
}
